import { createFunctionLibrary } from '@wix/thunderbolt-becky-root'
import type {
	BeckyFunctionLibrary,
	BeckyModel,
	BeckyModelFactory,
	FeaturesModelInstance,
	SiteAssetsModule,
} from '@wix/thunderbolt-becky-root'
import { typedModelFactory } from './helpers/typedModelFactory'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import byRefModule from '../byRef'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { deStringifyExperiments } from './helpers/experimentsParser'
import { fetchShapes } from '../findShapes/fetchShapes'

export const featuresModelFactory = typedModelFactory<FeaturesModelInstance>(require('../carmi/features.carmi'))
const findShapesModelFactory: BeckyModelFactory<
	BeckyModel,
	BeckyFunctionLibrary,
	{ shapeIds: Array<string> }
> = require('../findShapes/carmi/findShapes.carmi')

const featuresModule: SiteAssetsModule<FeaturesModelInstance> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const experiments = deStringifyExperiments(payload.beckyExperiments)
			const beckyTopology = getBeckyTopology(
				topologyGetter,
				payload,
				experiments['specs.thunderbolt.relativeMediaRoot']
			)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) =>
					resolveBeckyModel({
						payload: payloadWithResolvedRefs,
						beckyTopology,
						fetchFn,
						metricsReporter,
					})
				)
				.then(async (beckyModel) => {
					const shouldPreFetchShapes = experiments['specs.thunderbolt.prefetchComponentsShapesInBecky']

					const { shapeIds } = shouldPreFetchShapes
						? funcLib.runAndReport(
								() => findShapesModelFactory(beckyModel, funcLib),
								'tb-features-find-shapes'
						  )
						: { shapeIds: [] as Array<string> }

					const shapeStore = await funcLib.runAsyncAndReport(
						async () =>
							fetchShapes(
								shapeIds ? shapeIds : [],
								fetchFn,
								beckyTopology.environmentTopology.mediaRootUrl,
								funcLib.buildSvgUrl,
								metricsReporter
							),
						'tb-features-fetch-shapes'
					)
					const beckyModelWithShapes: BeckyModel = { ...beckyModel, shapeStore }

					return funcLib.runAsyncAndReport(() => {
						return new Promise((done) => featuresModelFactory(beckyModelWithShapes, { ...funcLib, done }))
					}, 'tb-features-model-calculation')
				})
		},
	}
}

export default featuresModule
